// @ts-nocheck
import { Stack, Title, Text as MantineText, Code, Group, Divider } from '@mantine/core';
import { IconStar, IconHeart, IconBrandGithub } from '@tabler/icons-react';
import { Card, Button, Text, Badge, Anchor, Kbd } from '../../../components/ui';

export default function CorePage() {
  return (
    <Stack gap="xl">
      {/* Page Header */}
      <Stack gap="xs">
        <Title order={2}>Core UI Components</Title>
        <MantineText c="dimmed" size="sm">
          Essential building blocks for creating interfaces - buttons, text, badges, and basic elements
        </MantineText>
      </Stack>

      {/* Button Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Button</Title>
            <MantineText size="sm" c="dimmed">
              Interactive button component with multiple variants including gradient styles
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Default Variants:</MantineText>
            <Group>
              <Button variant="filled">Filled Button</Button>
              <Button variant="light">Light Button</Button>
              <Button variant="outline">Outline Button</Button>
              <Button variant="subtle">Subtle Button</Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Button variant="filled">Filled Button</Button>
<Button variant="light">Light Button</Button>
<Button variant="outline">Outline Button</Button>
<Button variant="subtle">Subtle Button</Button>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Gradient Variants:</MantineText>
            <Group>
              <Button variant="gradient" gradientVariant="primary">
                Primary Gradient
              </Button>
              <Button variant="gradient" gradientVariant="secondary">
                Secondary Gradient
              </Button>
              <Button variant="gradient" gradientVariant="accent">
                Accent Gradient
              </Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Button variant="gradient" gradientVariant="primary">
  Primary Gradient
</Button>
<Button variant="gradient" gradientVariant="secondary">
  Secondary Gradient
</Button>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>With Icons:</MantineText>
            <Group>
              <Button leftSection={<IconStar size={16} />}>With Icon</Button>
              <Button variant="gradient" gradientVariant="primary" rightSection={<IconHeart size={16} />}>
                Icon Right
              </Button>
              <Button variant="light" leftSection={<IconBrandGithub size={16} />}>
                GitHub
              </Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Button leftSection={<IconStar size={16} />}>
  With Icon
</Button>
<Button variant="gradient" gradientVariant="primary" rightSection={<IconHeart size={16} />}>
  Icon Right
</Button>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • variant - 'filled' | 'light' | 'outline' | 'subtle' | 'gradient'
              <br />
              • gradientVariant - 'primary' | 'secondary' | 'accent' (when variant="gradient")
              <br />
              • leftSection / rightSection - Icon or element to display
              <br />
              • size - 'xs' | 'sm' | 'md' | 'lg' | 'xl'
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* Text Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Text</Title>
            <MantineText size="sm" c="dimmed">
              Enhanced text component with gradient support and theme-aware styling
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Regular Text:</MantineText>
            <Stack gap="xs">
              <Text size="xs">Extra small text</Text>
              <Text size="sm">Small text</Text>
              <Text size="md">Medium text (default)</Text>
              <Text size="lg">Large text</Text>
              <Text size="xl">Extra large text</Text>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Text size="xs">Extra small text</Text>
<Text size="sm">Small text</Text>
<Text size="md">Medium text (default)</Text>
<Text size="lg">Large text</Text>
<Text size="xl">Extra large text</Text>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Gradient Text:</MantineText>
            <Stack gap="xs">
              <Text gradient gradientVariant="primary" size="xl" fw={700}>
                Primary Gradient Text
              </Text>
              <Text gradient gradientVariant="secondary" size="lg" fw={600}>
                Secondary Gradient Text
              </Text>
              <Text gradient gradientVariant="accent" size="md" fw={500}>
                Accent Gradient Text
              </Text>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Text gradient gradientVariant="primary" size="xl" fw={700}>
  Primary Gradient Text
</Text>
<Text gradient gradientVariant="secondary" size="lg" fw={600}>
  Secondary Gradient Text
</Text>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • gradient - boolean to enable gradient text
              <br />
              • gradientVariant - 'primary' | 'secondary' | 'accent'
              <br />
              • size - 'xs' | 'sm' | 'md' | 'lg' | 'xl'
              <br />
              • fw - font weight (100-900)
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* Badge Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Badge</Title>
            <MantineText size="sm" c="dimmed">
              Small label component for displaying status, counts, or categories
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Default Variants:</MantineText>
            <Group>
              <Badge variant="filled">Filled</Badge>
              <Badge variant="light">Light</Badge>
              <Badge variant="outline">Outline</Badge>
              <Badge variant="dot">Dot Badge</Badge>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Badge variant="filled">Filled</Badge>
<Badge variant="light">Light</Badge>
<Badge variant="outline">Outline</Badge>
<Badge variant="dot">Dot Badge</Badge>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Different Colors:</MantineText>
            <Group>
              <Badge color="blue">Blue</Badge>
              <Badge color="green">Green</Badge>
              <Badge color="red">Red</Badge>
              <Badge color="orange">Orange</Badge>
              <Badge color="violet">Violet</Badge>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Badge color="blue">Blue</Badge>
<Badge color="green">Green</Badge>
<Badge color="red">Red</Badge>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Sizes:</MantineText>
            <Group align="center">
              <Badge size="xs">Extra Small</Badge>
              <Badge size="sm">Small</Badge>
              <Badge size="md">Medium</Badge>
              <Badge size="lg">Large</Badge>
              <Badge size="xl">Extra Large</Badge>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Badge size="xs">Extra Small</Badge>
<Badge size="sm">Small</Badge>
<Badge size="md">Medium</Badge>
<Badge size="lg">Large</Badge>
<Badge size="xl">Extra Large</Badge>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Anchor Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Anchor</Title>
            <MantineText size="sm" c="dimmed">
              Styled link component with gradient support
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Examples:</MantineText>
            <Stack gap="xs">
              <Anchor href="#" underline="hover">
                Default anchor with hover underline
              </Anchor>
              <Anchor href="#" underline="always">
                Always underlined anchor
              </Anchor>
              <Anchor href="#" gradient gradientVariant="primary" fw={600}>
                Gradient anchor link
              </Anchor>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Anchor href="#" underline="hover">
  Default anchor with hover underline
</Anchor>
<Anchor href="#" gradient gradientVariant="primary" fw={600}>
  Gradient anchor link
</Anchor>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Kbd Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Kbd (Keyboard Key)</Title>
            <MantineText size="sm" c="dimmed">
              Display keyboard shortcuts and key combinations
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Examples:</MantineText>
            <Stack gap="xs">
              <div>
                <MantineText size="sm" span>
                  Press <Kbd>Ctrl</Kbd> + <Kbd>K</Kbd> to open command palette
                </MantineText>
              </div>
              <div>
                <MantineText size="sm" span>
                  Use <Kbd>⌘</Kbd> + <Kbd>S</Kbd> to save
                </MantineText>
              </div>
              <div>
                <MantineText size="sm" span>
                  Navigate with <Kbd>↑</Kbd> <Kbd>↓</Kbd> arrow keys
                </MantineText>
              </div>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Text size="sm" span>
  Press <Kbd>Ctrl</Kbd> + <Kbd>K</Kbd> to open command palette
</Text>

<Text size="sm" span>
  Use <Kbd>⌘</Kbd> + <Kbd>S</Kbd> to save
</Text>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Code Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Code</Title>
            <MantineText size="sm" c="dimmed">
              Display inline or block code snippets with syntax highlighting
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Inline Code:</MantineText>
            <MantineText size="sm">
              Use the <Code>useState</Code> hook to manage component state. Import it with{' '}
              <Code>import {`{ useState }`} from 'react'</Code>
            </MantineText>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Text size="sm">
  Use the <Code>useState</Code> hook to manage component state.
</Text>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Block Code:</MantineText>
            <Code block language="tsx">
{`function Example() {
  const [count, setCount] = useState(0);

  return (
    <Button onClick={() => setCount(count + 1)}>
      Count: {count}
    </Button>
  );
}`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Usage:</MantineText>
            <Code block language="tsx">
{`<Code block language="tsx">
{\`function Example() {
  const [count, setCount] = useState(0);
  return <Button>Count: {count}</Button>;
}\`}
</Code>`}
            </Code>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
